import React from "react";
import { Tag, Typography } from "antd";
import routes from "../../../../engine/routes";
import { CONFIG_LABELS, fmtDate } from "../institutionMerge.constants";

const { Text } = Typography;

const OID = /^[a-f0-9]{24}$/i;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const MODE_LABEL = { multiple: "groupée", unique: "par prestation" };
const MONTHS = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
];

/** Collection → the word the operator uses. */
export const MODEL_LABELS = {
  Institution: "Institution",
  User: "Compte",
  Address: "Adresse",
  Responsable: "Responsable",
  Prestation: "Prestation",
  Bill: "Facture",
  Batch: "Lot de factures",
  Principal: "Mandant",
};
export const MODEL_ORDER = Object.keys(MODEL_LABELS);
const MODEL_COLORS = {
  Institution: "purple",
  User: "geekblue",
  Address: "cyan",
  Responsable: "green",
  Prestation: "blue",
  Bill: "gold",
  Batch: "orange",
  Principal: "magenta",
};
export const modelLabel = m => MODEL_LABELS[m] || m || "—";
export const ModelTag = ({ model }) => (
  <Tag color={MODEL_COLORS[model]} style={{ marginRight: 0 }}>
    {modelLabel(model)}
  </Tag>
);

/** Journal vocabulary (same words as the Journal tab). */
export const PHASES = {
  target: "cible",
  refs: "références",
  blacklists: "listes noires",
  users: "comptes",
  dependencies: "dépendances",
  archive: "archivage",
  restore: "restauration",
  detach: "détachement",
};
export const OPS = {
  set_scalar: "champ",
  set_array: "liste",
  update_doc: "document",
  insert_doc: "création",
  delete_doc: "suppression",
};

/** Institution fields the config meta does not describe (system fields of the full document). */
export const EXTRA_LABELS = {
  name: "Nom",
  billings: "Règles de facturation",
  archived: "Archivée",
  mergedInto: "Fusionnée dans",
  mergedAt: "Fusionnée le",
  residualOfMerge: "Résidu de fusion",
  customerId: "Client",
  creationDate: "Créée le",
  lastAction: "Dernière action",
  createdAt: "Créée le",
  updatedAt: "Modifiée le",
};
export const HIDDEN_FIELDS = ["_id", "__v", "id", "hash", "salt"];
export const fieldLabel = (path, meta) => {
  const m = (meta || []).find(x => x.field === path);
  return (m && m.label) || CONFIG_LABELS[path] || EXTRA_LABELS[path] || path;
};

const isRule = x => !!x && typeof x === "object" && "year" in x && "month" in x;
const ruleText = r =>
  `${MONTHS[r.month - 1] || r.month} ${r.year} — ${MODE_LABEL[r.type] ||
    r.type ||
    "?"}`;

/**
 * A stored value as the operator reads it: empty → "—", booleans → oui/non, ids → their
 * name (from `labels`), `{_id,name}` refs → name, ISO dates → fr-CH, billing rules →
 * « juin 2025 — groupée », notification → « clôture prestation : oui ». Never a bare id
 * when a name is known; never JSON when a readable form exists.
 */
export const show = (v, labels = {}) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "oui" : "non";
  if (typeof v === "number") return String(v);
  if (Array.isArray(v)) {
    if (!v.length) return "—";
    return v.map(x => show(x, labels)).join(", ");
  }
  if (typeof v === "string") {
    if (OID.test(v)) return labels[v] || `…${v.slice(-6)}`;
    if (ISO_DATE.test(v)) return fmtDate(v);
    return v;
  }
  if (typeof v === "object") {
    if ("cloturePrestation" in v)
      return `clôture prestation : ${v.cloturePrestation ? "oui" : "non"}`;
    if (isRule(v)) return ruleText(v);
    if (v.name || v.label) return v.name || v.label;
    if (v._id) return labels[String(v._id)] || `…${String(v._id).slice(-6)}`;
    const entries = Object.entries(v);
    if (!entries.length) return "—";
    return entries.map(([k, x]) => `${k} : ${show(x, labels)}`).join(" · ");
  }
  return String(v);
};

/** Comparison key: "" / false / [] / null are one and the same "empty"; refs compare by id. */
export const norm = v => {
  if (v === null || v === undefined || v === "" || v === false) return "";
  if (Array.isArray(v))
    return v.length ? JSON.stringify(v.map(norm).sort()) : "";
  if (typeof v === "object") {
    const keys = Object.keys(v).filter(k => k !== "_id" && k !== "id");
    if (
      v._id &&
      !isRule(v) &&
      (keys.length === 0 || keys.every(k => ["name", "label"].includes(k)))
    )
      return String(v._id);
    return JSON.stringify(
      keys.sort().reduce((o, k) => ({ ...o, [k]: norm(v[k]) }), {}),
    );
  }
  return String(v);
};
export const same = (a, b) => norm(a) === norm(b);

/** Before value (struck through, grey) — only struck when it actually changed. */
export const Before = ({ value, changed = true, labels }) =>
  changed ? (
    <Text delete type="secondary">
      {show(value, labels)}
    </Text>
  ) : (
    <Text>{show(value, labels)}</Text>
  );
/** After value (bold) — bold only when it changed. */
export const After = ({ value, changed = true, labels }) =>
  changed ? (
    <Text strong>{show(value, labels)}</Text>
  ) : (
    <Text>{show(value, labels)}</Text>
  );

const arrKey = x =>
  x && typeof x === "object" && x._id ? String(x._id) : norm(x);
/** For arrays the operator wants the difference, not two 15-item lists: − removed / + added. */
export const arrayDiff = (before, after) => {
  const b = Array.isArray(before) ? before : [];
  const a = Array.isArray(after) ? after : [];
  const bk = b.map(arrKey);
  const ak = a.map(arrKey);
  const removed = b.filter((x, i) => !ak.includes(bk[i]));
  const added = a.filter((x, i) => !bk.includes(ak[i]));
  return { removed, added, unchanged: b.length - removed.length };
};
export const ArrayDiff = ({ before, after, labels }) => {
  const d = arrayDiff(before, after);
  if (!d.removed.length && !d.added.length)
    return <Text type="secondary">liste inchangée</Text>;
  return (
    <span>
      {d.removed.map((x, i) => (
        <Tag key={`r${i}`} color="red">
          − {show(x, labels)}
        </Tag>
      ))}
      {d.added.map((x, i) => (
        <Tag key={`a${i}`} color="green">
          + {show(x, labels)}
        </Tag>
      ))}
      {d.unchanged > 0 && (
        <Text type="secondary">{d.unchanged} inchangé(s)</Text>
      )}
    </span>
  );
};

/**
 * One change on one line: « label : ~~avant~~ → **après** » — arrays as − / + diff.
 * `before`/`after` are display-ready values (ids resolved server-side, or via `labels`).
 */
export const ChangeLine = ({ label, before, after, labels }) => {
  const arrays = Array.isArray(before) || Array.isArray(after);
  return (
    <div style={{ lineHeight: 1.7 }}>
      <Text type="secondary">{label} : </Text>
      {arrays ? (
        <ArrayDiff before={before} after={after} labels={labels} />
      ) : (
        <>
          <Before value={before} labels={labels} />
          <Text type="secondary"> → </Text>
          <After value={after} labels={labels} />
        </>
      )}
    </div>
  );
};

/** antd column props that colour the Avant / Après columns (stylesheet: merge.scss). */
export const beforeCol = {
  onHeaderCell: () => ({ className: "merge-col-before" }),
  onCell: () => ({ className: "merge-col-before" }),
};
export const afterCol = {
  onHeaderCell: () => ({ className: "merge-col-after" }),
  onCell: () => ({ className: "merge-col-after" }),
};

const PROVENANCE_COLORS = {
  référence: "default",
  "choix manuel": "blue",
  "valeur saisie": "purple",
  identique: "default",
};
/** Where the resulting value came from: référence / choix manuel / valeur saisie / identique. */
export const ProvenanceTag = ({ value }) =>
  value ? (
    <Tag
      color={PROVENANCE_COLORS[value] || "default"}
      style={{ marginRight: 0, opacity: value === "identique" ? 0.7 : 1 }}
    >
      {value}
    </Tag>
  ) : null;

/** State tags of a touched document. */
export const StateTags = ({ item }) => (
  <>
    {item.createdByMerge && (
      <Tag color="blue" style={{ marginRight: 0 }}>
        créé par la fusion
      </Tag>
    )}
    {item.deletedByMerge && (
      <Tag color="red" style={{ marginRight: 0 }}>
        supprimé par la fusion
      </Tag>
    )}
    {item.restored && (
      <Tag color="purple" style={{ marginRight: 0 }}>
        restauré
      </Tag>
    )}
    {item.exists === false && !item.deletedByMerge && (
      <Tag style={{ marginRight: 0 }}>
        {item.createdByMerge ? "supprimé·e à la restauration" : "n'existe plus"}
      </Tag>
    )}
  </>
);

/** Front route of a touched document, when its model has a page (the server only sends model + id). */
export const recordLink = (model, id) => {
  if (!id) return null;
  try {
    switch (model) {
      case "Prestation":
        return routes.prestations(id).detail.path;
      case "Bill":
        return routes.billing(id).detail.path;
      case "Institution":
        return routes.institutions(id).detail.path;
      case "Principal":
        return routes.principals(id).detail.path;
      default:
        return null;
    }
  } catch (e) {
    return null;
  }
};
